import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Sparkles, Loader2, Car, Scale, FileText, Handshake, RefreshCw, ChevronDown,
  CheckCircle2, AlertTriangle, Lightbulb, ShieldCheck, Copy,
} from "lucide-react";

const MODULES = [
  { key: "reconstruction", name: "AI Crash Reconstruction", icon: Car, desc: "How well the evidence supports expert reconstruction" },
  { key: "liability", name: "AI Liability Analysis", icon: Scale, desc: "Liability factors the documentation records" },
  { key: "demand", name: "AI Demand Package Builder", icon: FileText, desc: "Structured demand-package draft for review" },
  { key: "negotiation", name: "AI Insurance Negotiation", icon: Handshake, desc: "Evidence-grounded negotiation talking points" },
];

const READY = { high: "bg-emerald-500/15 text-emerald-300", moderate: "bg-amber-500/15 text-amber-300", limited: "bg-red-500/15 text-red-300" };

const List = ({ items, tone = "slate", icon: Icon }) => (
  <ul className="mt-1 space-y-1">
    {(items || []).map((t, i) => (
      <li key={i} className={`text-sm flex items-start gap-2 ${tone === "amber" ? "text-amber-200/90" : tone === "emerald" ? "text-emerald-200/90" : "text-slate-300"}`}>
        {Icon ? <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${tone === "amber" ? "text-amber-400" : tone === "emerald" ? "text-emerald-400" : "text-cyan-400"}`} />
          : <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${tone === "amber" ? "bg-amber-400" : tone === "emerald" ? "bg-emerald-400" : "bg-cyan-400"}`} />}
        <span>{typeof t === "string" ? t : JSON.stringify(t)}</span>
      </li>
    ))}
  </ul>
);

const Group = ({ title, children }) => (
  <div className="mt-3">
    <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">{title}</div>
    {children}
  </div>
);

function ModuleBody({ moduleKey, data }) {
  if (data?._parse_error) return <p className="text-sm text-slate-300 whitespace-pre-wrap">{data.summary}</p>;
  const copy = () => { navigator.clipboard?.writeText(JSON.stringify(data, null, 2)); toast.success("Copied to clipboard"); };
  return (
    <div className="space-y-1" data-testid={`evai-body-${moduleKey}`}>
      {data.readiness && <span className={`inline-block text-[11px] px-2 py-0.5 rounded-full font-semibold mb-1 ${READY[data.readiness] || "bg-slate-700 text-slate-300"}`}>Reconstruction readiness: {data.readiness}</span>}
      {data.summary && <p className="text-sm text-slate-200 leading-relaxed">{data.summary}</p>}

      {moduleKey === "reconstruction" && (<>
        {data.documented_elements?.length > 0 && <Group title="Documented elements"><div className="mt-1 flex flex-wrap gap-1.5">{data.documented_elements.map((e, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-slate-800 text-slate-300">{e}</span>)}</div></Group>}
        {data.supports?.length > 0 && <Group title="Supports analysis of"><List items={data.supports} tone="emerald" icon={CheckCircle2} /></Group>}
        {data.gaps?.length > 0 && <Group title="Gaps limiting reconstruction"><List items={data.gaps} tone="amber" icon={AlertTriangle} /></Group>}
        {data.recommended_captures?.length > 0 && <Group title="Recommended additional captures"><List items={data.recommended_captures} icon={Lightbulb} /></Group>}
      </>)}

      {moduleKey === "liability" && (<>
        {data.documented_factors?.length > 0 && <Group title="Documented factors">
          <div className="mt-1 space-y-1.5">{data.documented_factors.map((f, i) => (
            <div key={i} className="text-sm"><span className="text-slate-100 font-medium">{f.factor}</span><span className="text-slate-400"> — {f.observation}</span></div>
          ))}</div></Group>}
        {data.supported_by_evidence?.length > 0 && <Group title="Supported by evidence"><List items={data.supported_by_evidence} tone="emerald" icon={CheckCircle2} /></Group>}
        {data.disputed_or_unclear?.length > 0 && <Group title="Unclear / undocumented"><List items={data.disputed_or_unclear} tone="amber" icon={AlertTriangle} /></Group>}
        {data.documentation_recommendations?.length > 0 && <Group title="Recommendations"><List items={data.documentation_recommendations} icon={Lightbulb} /></Group>}
      </>)}

      {moduleKey === "demand" && (<>
        {data.sections?.length > 0 && <Group title="Draft sections">
          <div className="mt-1 space-y-2">{data.sections.map((s, i) => (
            <div key={i} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3"><div className="text-sm font-semibold text-cyan-300">{s.heading}</div><p className="text-sm text-slate-300 mt-1 whitespace-pre-wrap leading-relaxed">{s.content}</p></div>
          ))}</div></Group>}
        {data.evidence_inventory?.length > 0 && <Group title="Evidence inventory"><List items={data.evidence_inventory} /></Group>}
        {data.strength_indicators?.length > 0 && <Group title="Strength indicators"><List items={data.strength_indicators} tone="emerald" icon={CheckCircle2} /></Group>}
        {data.outstanding_before_sending?.length > 0 && <Group title="Outstanding before sending"><List items={data.outstanding_before_sending} tone="amber" icon={AlertTriangle} /></Group>}
      </>)}

      {moduleKey === "negotiation" && (<>
        {data.talking_points?.length > 0 && <Group title="Talking points">
          <div className="mt-1 space-y-1.5">{data.talking_points.map((t, i) => (
            <div key={i} className="text-sm"><span className="text-slate-100">• {t.point}</span>{t.supporting_evidence && <span className="text-slate-500 text-xs block ml-3">↳ {t.supporting_evidence}</span>}</div>
          ))}</div></Group>}
        {data.evidence_strengths?.length > 0 && <Group title="Evidence strengths"><List items={data.evidence_strengths} tone="emerald" icon={CheckCircle2} /></Group>}
        {data.evidence_gaps_to_address?.length > 0 && <Group title="Gaps to address"><List items={data.evidence_gaps_to_address} tone="amber" icon={AlertTriangle} /></Group>}
        {data.anticipated_insurer_challenges?.length > 0 && <Group title="Anticipated insurer challenges">
          <div className="mt-1 space-y-1.5">{data.anticipated_insurer_challenges.map((c, i) => (
            <div key={i} className="text-sm"><span className="text-amber-200/90">{c.challenge}</span><span className="text-slate-400 block text-xs ml-3">↳ {c.response}</span></div>
          ))}</div></Group>}
      </>)}

      {(data.note || data.disclaimer) && <p className="mt-3 text-[11px] text-slate-500 italic">{data.note || data.disclaimer}</p>}
      <button onClick={copy} className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300"><Copy className="w-3.5 h-3.5" /> Copy</button>
    </div>
  );
}

export default function EvidenceAIStudio({ caseId: propCaseId }) {
  const params = useParams();
  const caseId = propCaseId || params.caseId;
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get(`/cases/${caseId}/evidence-ai`).then((r) => setResults(r.data || {})).catch(() => setResults({}));
  }, [caseId]);

  const run = async (key) => {
    setBusy(key);
    const force = !!results[key]?.data; // explicit Regenerate forces a fresh LLM run; first Generate uses cache-or-create
    try {
      const { data } = await api.post(`/cases/${caseId}/evidence-ai/${key}${force ? "?force=true" : ""}`);
      setResults((r) => ({ ...r, [key]: data }));
      setOpen(key);
      if (data.data) toast.success("Analysis ready"); else toast.message(data.message || "No evidence yet");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(null); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="mt-8 rounded-3xl border border-slate-800 bg-navy text-slate-100 p-6" data-testid="evidence-ai-studio">
      <p className="text-xs font-medium text-cyan-400 uppercase tracking-widest flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Evidence AI Studio™</p>
      <h2 className="font-heading text-2xl font-extrabold text-white mt-1">AI analysis on your evidence</h2>
      <p className="text-sm text-slate-400 mt-1">Documentation-quality assessments powered by the Evidence Intelligence engine. Not legal advice.</p>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        {MODULES.map(({ key, name, icon: Icon, desc }) => {
          const res = results[key];
          const data = res?.data;
          const isOpen = open === key;
          return (
            <div key={key} className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4" data-testid={`evai-card-${key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  <div className="h-9 w-9 rounded-xl bg-cyan-500/15 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-cyan-400" /></div>
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-white text-sm">{name}</div>
                    <div className="text-xs text-slate-400">{desc}</div>
                  </div>
                </div>
                <button onClick={() => run(key)} disabled={busy === key} data-testid={`evai-run-${key}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500 text-slate-950 text-xs font-semibold hover:bg-cyan-400 disabled:opacity-50 shrink-0">
                  {busy === key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : data ? <RefreshCw className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {data ? "Regenerate" : "Generate"}
                </button>
              </div>

              {res && (
                <div className="mt-3 pt-3 border-t border-slate-800">
                  {res.message && !data ? (
                    <p className="text-xs text-slate-400 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> {res.message}</p>
                  ) : data ? (
                    <>
                      <button onClick={() => setOpen(isOpen ? null : key)} data-testid={`evai-toggle-${key}`} className="flex items-center gap-1.5 text-xs text-cyan-300 hover:text-cyan-200">
                        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} /> {isOpen ? "Hide analysis" : "View analysis"}
                        {res.generated_at && <span className="text-slate-500">· {new Date(res.generated_at).toLocaleDateString()}</span>}
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden mt-2">
                            <ModuleBody moduleKey={key} data={data} />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
